import { FlattenedAdvPlayer } from '../Api/players/players.types'

export type HeadCell = {
  id: keyof FlattenedAdvPlayer
  label: string
  numeric: boolean
  minWidth: number
}
export type AdvancedStatsCategory = {
  [key: string]: AdvancedStatsCategoryItem
}
export type AdvancedStatsCategoryItem = {
  cat: string
  sortBy: string
  order: number
}

export const ADVANCED_STATS_YEARS = [2025, 2024, 2023, 2022, 2021, 2020, 2019]
export const MIN_TOUCHES = 20

export const mainNameHeader: HeadCell = {
  id: 'lastName',
  numeric: false,
  label: 'Name',
  minWidth: 110,
}

export const ADVANCED_STATS_CATEGORIES: AdvancedStatsCategory = {
  SCORING: { cat: 'scoring', sortBy: 'unassistedGoals', order: 1 },
  PASSING: { cat: 'passing', sortBy: 'assistOpportunities', order: 2 },
  SHOOTING: { cat: 'shooting', sortBy: 'unassistedShots', order: 3 },
  HANDEDNESS: { cat: 'handedness', sortBy: 'shots', order: 4 },
}

export const advScoringHeaders: HeadCell[] = [
  { id: 'goals', numeric: true, label: 'Total Goals', minWidth: 50 },
  { id: 'unassistedGoals', numeric: true, label: 'Unassisted Goals', minWidth: 50 },
  { id: 'assistedGoals', numeric: true, label: 'Assisted Goals', minWidth: 50 },
]

export const advPassingHeaders: HeadCell[] = [
  { id: 'assists', numeric: true, label: 'Total Assists', minWidth: 50 },
  { id: 'assistOpportunities', numeric: true, label: 'Assist Opp.', minWidth: 50 },
]

export const advShootingHeaders: HeadCell[] = [
  { id: 'shots', numeric: true, label: 'Total Shots', minWidth: 50 },
  { id: 'unassistedShots', numeric: true, label: 'Unassisted Shots', minWidth: 50 },
  { id: 'unassistedShotPct', numeric: true, label: 'Unassisted Shot %', minWidth: 50 },
  { id: 'assistedShots', numeric: true, label: 'Assisted Shots', minWidth: 50 },
  { id: 'assistedShotPct', numeric: true, label: 'Assisted Shot %', minWidth: 50 },
  { id: 'pipeShots', numeric: true, label: 'Pipe Shots', minWidth: 50 },
]

export const advTouchesHeaders: HeadCell[] = [
  { id: 'touches', numeric: true, label: 'Touches', minWidth: 50 },
  { id: 'goalRate', numeric: true, label: 'Goal Rate', minWidth: 50 },
  { id: 'assistRate', numeric: true, label: 'Assist Rate', minWidth: 50 },
  { id: 'passRate', numeric: true, label: 'Pass Rate', minWidth: 50 },
  { id: 'shotRate', numeric: true, label: 'Shot Rate', minWidth: 50 },
  { id: 'turnoverRate', numeric: true, label: 'Turnover Rate', minWidth: 50 },
]

export const advHandednessHeaders: HeadCell[] = [
  { id: 'shots', numeric: true, label: 'Total Shots', minWidth: 50 },
  { id: 'rhGoals', numeric: true, label: 'RH Goals', minWidth: 50 },
  { id: 'rhShots', numeric: true, label: 'RH Shots', minWidth: 50 },
  { id: 'rhShotPct', numeric: true, label: 'RH Shot %', minWidth: 50 },
  { id: 'lhGoals', numeric: true, label: 'LH Goals', minWidth: 50 },
  { id: 'lhShots', numeric: true, label: 'LH Shots', minWidth: 50 },
  { id: 'lhShotPct', numeric: true, label: 'LH Shot %', minWidth: 50 },
]

export const advancedStatsHeaders: HeadCell[] = [
  //Scoring
  ...advScoringHeaders,

  //Passing
  ...advPassingHeaders,

  //Shooting
  ...advShootingHeaders,

  //Touches
  ...advTouchesHeaders,

  //Handedness
  ...advHandednessHeaders,
]

export const getAdvStatsHeaders = (selections: string): HeadCell[] => {
  let headers: HeadCell[] = [
    { id: 'lastName', numeric: false, label: 'Name', minWidth: 110 },
  ]
  const selectedCategories = selections.split(',').map((item) => item.trim())

  if (selectedCategories.includes('scoring')) {
    headers = [...headers, ...advScoringHeaders]
  }
  if (selectedCategories.includes('passing')) {
    headers = [...headers, ...advPassingHeaders]
  }
  if (selectedCategories.includes('shooting')) {
    headers = [...headers, ...advShootingHeaders]
  }

  if (selectedCategories.includes('touches')) {
    headers = [...headers, ...advTouchesHeaders]
  }
  if (selectedCategories.includes('handedness')) {
    headers = [...headers, ...advHandednessHeaders]
  }

  return headers
}

export const touchesFooterItems: { [key: string]: string } = {
  touches:
    'Total Touches: Number of times the player touched the ball through the game.',
  goalRate: 'Goal Rate (goals/touches): Percentage of touches that resulted in a goal',
  assistRate:
    'Assist Rate (assists/touches): Percentage of touches that resulted in an assist',
  passRate:
    'Pass Rate (passes/touches): Percentage of touches that resulted in a completed pass',
  shotRate: 'Shot Rate (shots/touches): Percentage of touches that resulted in a shot',
  turnoverRate:
    'Turnover Rate (turnovers/touches): Percentage of touches that resulted in a turnover',
}
